/**
 * Simple Wizard Shell
 * 
 * Clean, simple wizard layout using single Zustand store.
 * Replaces the complex WizardShell with over-engineered navigation.
 */

import React, { useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { toast } from '@etherisc/ui-kit';
import type { WizardConfig, WizardData, WizardStepProps } from '../types/WizardTypes';
import { createSimpleWizardStore } from '../stores/useSimpleWizardStore';

export interface SimpleWizardShellProps<TData extends WizardData> {
  config: WizardConfig<TData>;
  initialData: TData;
  onSubmitSuccess?: (data: TData) => void;
  onSubmitError?: (error: Error) => void;
}

export function SimpleWizardShell<TData extends WizardData>({
  config,
  initialData,
  onSubmitSuccess,
  onSubmitError,
}: SimpleWizardShellProps<TData>) {
  
  // Create store instance
  const useStore = React.useMemo(() => createSimpleWizardStore<TData>(), []);
  const wizard = useStore();
  
  // Initialize wizard once
  const [isInitialized, setIsInitialized] = React.useState(false);
  useEffect(() => {
    if (!isInitialized) {
      wizard.initialize(config, initialData);
      setIsInitialized(true);
    }
  }, [wizard, config, initialData, isInitialized]);
  
  if (!wizard.config) {
    return <div>Loading...</div>;
  }
  
  // Current step info
  const currentStepIndex = wizard.currentStep - 1; // Convert to 0-based
  const currentStepConfig = wizard.config?.steps[currentStepIndex];
  
  if (!currentStepConfig) {
    return <div>Invalid step</div>;
  }
  
  // Progress calculation
  const progress = {
    current: wizard.currentStep,
    total: wizard.config?.steps.length,
    percentage: `${Math.round((wizard.currentStep / wizard.config?.steps.length) * 100)}%`,
  };
  
  // Navigation handlers
  const handleNext = async () => {
    // First, validate the current step
    const stepConfig = wizard.config?.steps[wizard.currentStep - 1];
    if (stepConfig?.validation) {
      const validationResult = stepConfig.validation(wizard.data);
      if (!validationResult.isValid) {
        // Log validation errors (no toast - errors will be shown in form)
        console.log('🔍 VALIDATION: Cannot navigate - current step invalid:', validationResult.errors);
        return; // Don't navigate if current step is invalid
      }
    }
    
    if (wizard.config && wizard.currentStep < wizard.config?.steps.length) {
      // Auto-save before navigating to next step (especially important for Step 1 -> Step 2)
      if (wizard.isDirty) {
        try {
          const persistenceAdapter = (config as unknown as Record<string, unknown>).persistenceAdapter;
          if (persistenceAdapter) {
            const draftData = {
              id: 'quote-wizard-draft',
              wizardId: 'quote-wizard',
              data: {
                ...wizard.data,
                currentWizardStep: wizard.currentStep + 1  // Next step we're going to
              },
              createdAt: new Date(),
              updatedAt: new Date(),
              // Note: Removed metadata - not used for restoration, framework handles step tracking
            };
            
            await (persistenceAdapter as { saveDraft: (data: unknown) => Promise<unknown> }).saveDraft(draftData);
            
            // Check if a new quote was created during save and update wizard data
            const draftDataWithQuote = draftData.data as unknown as Record<string, unknown>;
            const wizardDataWithQuote = wizard.data as unknown as Record<string, unknown>;
            
            const draftQuote = draftDataWithQuote.quote as Record<string, unknown> | undefined;
            const wizardQuote = wizardDataWithQuote.quote as Record<string, unknown> | undefined;
            
            if (draftQuote?.id && draftQuote.id !== wizardQuote?.id) {
              wizard.updateData({ 
                quote: draftDataWithQuote.quote 
              } as unknown as Partial<TData>);
            }
            
            wizard.markClean();
          }
        } catch (error) {
          console.error('Failed to save draft before navigation:', error);
          toast.error('Failed to save progress. Please try again.');
          return; // Don't navigate if save failed
        }
      }

      const nextStepId = wizard.config?.steps[wizard.currentStep]?.id;
      if (nextStepId) {
        wizard.validateStep(nextStepId); // Validate next step
        wizard.goToStep(wizard.currentStep + 1);
      }
    }
  };
  
  const handlePrevious = () => {
    if (wizard.currentStep > 1) {
      wizard.goToStep(wizard.currentStep - 1);
    }
  };
  
  const handleSubmit = async () => {
    try {
      await wizard.config?.onSubmit(wizard.data);
      onSubmitSuccess?.(wizard.data);
    } catch (error) {
      onSubmitError?.(error as Error);
    }
  };
  
  // Run validation for current step to get errors
  const stepConfigForValidation = wizard.config?.steps[wizard.currentStep - 1];
  const validationResult = stepConfigForValidation?.validation ? stepConfigForValidation.validation(wizard.data) : { isValid: true, errors: [] };
  
  // Note: Validation and next button working correctly
  
  // Note: Removed debug logs - validation working correctly
  
  // Render current step with validation
  const stepProps: WizardStepProps<TData> = {
    data: wizard.data,
    updateData: wizard.updateData,
    errors: validationResult.errors,
    warnings: validationResult.warnings || [],
    isValid: validationResult.isValid,
    canGoNext: validationResult.isValid && wizard.currentStep < (wizard.config?.steps.length || 0),
    canGoPrevious: wizard.currentStep > 1,
    goToNext: handleNext,
    goToPrevious: handlePrevious,
    goToStep: (stepId: string) => {
      const stepIndex = wizard.config?.steps.findIndex(s => s.id === stepId);
      if (stepIndex !== undefined && stepIndex >= 0) {
        wizard.goToStep(stepIndex + 1);
      }
    },
  };
  
  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* 1. Progress Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Step {progress.current} of {progress.total}: {currentStepConfig.title}
          </h2>
          {wizard.isDirty && (
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
          )}
        </div>
        
        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: progress.percentage }}
          />
        </div>
        
        {/* Step indicators */}
        <div className="flex justify-between">
          {wizard.config?.steps.map((step, index) => {
            const stepNumber = index + 1;
            const canNavigate = wizard.canNavigateToStep(stepNumber);
            
            return (
              <button
                key={step.id}
                onClick={() => {
                  if (canNavigate) {
                    wizard.goToStep(stepNumber);
                  }
                }}
                disabled={!canNavigate}
                className={`flex flex-col items-center space-y-2 transition-colors ${
                  canNavigate 
                    ? 'text-blue-600 hover:text-blue-700 cursor-pointer' 
                    : 'text-gray-400 opacity-50 cursor-not-allowed'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  stepNumber < wizard.currentStep 
                    ? 'bg-blue-600 text-white' 
                    : stepNumber === wizard.currentStep
                      ? 'bg-blue-100 text-blue-600 border-2 border-blue-600'
                      : 'bg-gray-200 text-gray-500'
                }`}>
                  {stepNumber < wizard.currentStep ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    stepNumber
                  )}
                </div>
                <span className="text-xs text-center max-w-20">{step.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. Step Content Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {React.createElement(currentStepConfig.component, stepProps)}
        
        {/* Validation Errors Display - positioned at bottom like other steps */}
        {!validationResult.isValid && validationResult.errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-6">
            <h6 className="text-sm font-medium text-red-800 mb-2">Please resolve the following issues:</h6>
            <ul className="text-sm text-red-700 space-y-1">
              {validationResult.errors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* 3. Navigation Footer Card */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        {/* Previous Button */}
        <div>
          {wizard.currentStep > 1 && (
            <button
              onClick={handlePrevious}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              Previous
            </button>
          )}
        </div>

        {/* Save as Draft + Next/Submit */}
        <div className="flex items-center gap-3">
          {/* Save as Draft */}
          <button
            onClick={async () => {
              try {
                // Use the existing persistence system
                const persistenceAdapter = (config as unknown as Record<string, unknown>).persistenceAdapter;
                if (persistenceAdapter) {
                  const draftData = {
                    id: 'quote-wizard-draft',
                    wizardId: 'quote-wizard',
                    data: wizard.data,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    // Note: Removed metadata - not used for restoration, framework handles step tracking
                  };
                  
                  await (persistenceAdapter as { saveDraft: (data: unknown) => Promise<unknown> }).saveDraft(draftData);
                  
                  // Check if a new quote was created during save and update wizard data
                  const draftDataWithQuote = draftData.data as Record<string, unknown>;
                  const wizardDataWithQuote = wizard.data as Record<string, unknown>;
                  
                  const draftQuote = draftDataWithQuote.quote as Record<string, unknown> | undefined;
                  const wizardQuote = wizardDataWithQuote.quote as Record<string, unknown> | undefined;
                  
                  if (draftQuote?.id && draftQuote.id !== wizardQuote?.id) {
                    wizard.updateData({ 
                      quote: draftDataWithQuote.quote 
                    } as unknown as Partial<TData>);
                  }
                  
                  wizard.markClean();
                  toast.success('Draft saved successfully!');
                } else {
                  console.warn('No persistence adapter available');
                }
              } catch (error) {
                console.error('Failed to save draft:', error);
                toast.error('Failed to save draft. Please try again.');
              }
            }}
            disabled={!wizard.isDirty}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            💾 Save as Draft
          </button>

          {/* Next/Submit Button */}
          {wizard.currentStep < wizard.config?.steps.length ? (
            <button
              onClick={handleNext}
              disabled={!validationResult.isValid}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={wizard.isSubmitting}
              className="px-6 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {wizard.isSubmitting ? 'Submitting...' : 'Submit Quote'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
